'use client';

import { Check, Copy, Loader2, Mail, Share2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '../../atoms/button/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../dialog/dialog';

/** Esito della generazione di un invito (link + eventuale email inviata). */
export interface MemberInviteResult {
	inviteLink: string | null;
	emailSent?: boolean;
}

/** Esito dell'invio (o re-invio) dell'email di invito. */
export interface MemberInviteEmailResult {
	status: number;
	ok: boolean;
	error?: string;
	retryAfter?: number;
	inviteLink?: string | null;
}

export interface MemberInviteButtonProps {
	/** Id del socio da invitare. */
	memberId: string;
	/** Nome del socio, usato nei testi dell'email. */
	memberName?: string;
	/** Email del socio: se assente, l'invito non è disponibile. */
	email: string | null;
	/** Genera l'invito (link e/o email) per il socio. */
	onInvite: (memberId: string) => Promise<MemberInviteResult>;
	/** Invia (o re-invia) l'email di invito a partire dal link generato. */
	onSendInviteEmail: (args: {
		memberId?: string;
		link: string | null;
		memberName?: string;
	}) => Promise<MemberInviteEmailResult>;
	/** Nome applicazione usato nel testo dell'email. */
	appName?: string;
	/**
	 * Quando è partito l'ultimo invito (`members.invited_at`). Se c'è, il
	 * bottone dice «Reinvia invito» e mostra la data: prima restava «Invita»,
	 * identico a prima dell'invio (INB-375).
	 */
	invitedAt?: string | null;
	/** Etichetta del bottone trigger. */
	label?: string;
	/** Variante del bottone trigger. */
	variant?: React.ComponentProps<typeof Button>['variant'];
	/** Dimensione del bottone trigger. */
	size?: React.ComponentProps<typeof Button>['size'];
}

interface InviteState {
	open: boolean;
	link: string | null;
	email: string;
	memberName?: string;
	memberId?: string;
	emailSent?: boolean;
	sendingEmail?: boolean;
	rateLimitSeconds?: number;
}

/**
 * Bottone "Invita" con dialog di invito auto-contenuto: genera il link,
 * mostra l'esito, permette copia/condivisione e re-invio dell'email con
 * gestione del rate-limit. Sostituisce l'invito legato alla creazione socio.
 */
export function MemberInviteButton({
	memberId,
	memberName,
	email,
	onInvite,
	onSendInviteEmail,
	appName = 'CER',
	invitedAt = null,
	label = 'Invita',
	variant = 'outline',
	size = 'default',
}: MemberInviteButtonProps) {
	const [loading, setLoading] = useState(false);
	const [copied, setCopied] = useState(false);
	const [dialog, setDialog] = useState<InviteState>({ open: false, link: null, email: '' });
	const [sentAt, setSentAt] = useState<string | null>(invitedAt);
	useEffect(() => setSentAt(invitedAt), [invitedAt]);
	const rateLimitTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

	useEffect(() => {
		return () => {
			if (rateLimitTimerRef.current) clearInterval(rateLimitTimerRef.current);
		};
	}, []);

	async function startInvite() {
		if (!email) return;
		setLoading(true);
		try {
			const { inviteLink, emailSent } = await onInvite(memberId);
			setSentAt(new Date().toISOString());
			if (emailSent) toast.success(`Email di invito inviata a ${email}`);
			setCopied(false);
			setDialog({ open: true, link: inviteLink, email, memberName, memberId, emailSent });
		} catch (err) {
			// Surface the backend's actual reason (rate limit, Auth error, …) instead
			// of a fixed string — a silent catch-all here is what made the previous
			// failure undiagnosable without opening the browser console.
			const message = err instanceof Error && err.message ? err.message : undefined;
			toast.error(message ?? 'Generazione link invito fallita, riprova');
		} finally {
			setLoading(false);
		}
	}

	async function resendEmail() {
		setDialog((prev) => ({ ...prev, sendingEmail: true }));
		try {
			const json = await onSendInviteEmail({
				memberId: dialog.memberId,
				link: dialog.link,
				memberName: dialog.memberName,
			});
			if (json.status === 429) {
				const seconds: number = json.retryAfter ?? 60;
				setDialog((prev) => ({
					...prev,
					sendingEmail: false,
					rateLimitSeconds: seconds,
					link: json.inviteLink ?? prev.link,
				}));
				if (rateLimitTimerRef.current) clearInterval(rateLimitTimerRef.current);
				rateLimitTimerRef.current = setInterval(() => {
					setDialog((prev) => {
						const next = (prev.rateLimitSeconds ?? 0) - 1;
						if (next <= 0) {
							if (rateLimitTimerRef.current) clearInterval(rateLimitTimerRef.current);
							rateLimitTimerRef.current = null;
							return { ...prev, rateLimitSeconds: 0 };
						}
						return { ...prev, rateLimitSeconds: next };
					});
				}, 1000);
			} else if (!json.ok) {
				toast.error(json.error ?? 'Errore invio email');
				setDialog((prev) => ({ ...prev, sendingEmail: false }));
			} else {
				toast.success(`Email inviata a ${dialog.email}`);
				setDialog((prev) => ({ ...prev, open: false, sendingEmail: false }));
			}
		} catch {
			toast.error('Errore invio email');
			setDialog((prev) => ({ ...prev, sendingEmail: false }));
		}
	}

	const sentLabel = sentAt ? formatSentAt(sentAt) : null;

	return (
		<>
			{sentLabel && (
				<span className="self-center text-xs text-text-muted">Invito inviato il {sentLabel}</span>
			)}
			<Button
				type="button"
				variant={variant}
				size={size}
				onClick={startInvite}
				disabled={!email || loading}
				title={sentLabel ? `Ultimo invito: ${sentLabel}` : undefined}
			>
				{loading ? <Loader2 className="animate-spin" /> : <Mail />}
				{sentLabel ? 'Reinvia invito' : label}
			</Button>

			<Dialog open={dialog.open} onOpenChange={(open) => setDialog((prev) => ({ ...prev, open }))}>
				<DialogContent className="w-full max-w-sm border-border bg-card">
					<DialogHeader>
						<DialogTitle className="text-sm">Invito socio</DialogTitle>
					</DialogHeader>

					<p className="text-xs text-text-muted">
						Invito per <span className="font-medium text-text break-all">{dialog.email}</span>.
					</p>

					{dialog.emailSent && !dialog.link && (
						<div className="flex items-center gap-2 rounded-md bg-accent-green/10 px-3 py-2.5">
							<Check className="h-4 w-4 shrink-0 text-accent-green" />
							<p className="text-[13px] text-accent-green">
								Email di invito inviata a <strong>{dialog.email}</strong>. Il socio riceverà le
								istruzioni per accedere.
							</p>
						</div>
					)}

					{dialog.link && (
						<>
							<div className="rounded-md border border-border bg-bg p-2.5">
								<p className="break-all text-[12px] leading-relaxed text-text-muted font-mono">
									{dialog.link}
								</p>
								<button
									type="button"
									onClick={() => {
										navigator.clipboard.writeText(dialog.link ?? '');
										setCopied(true);
										setTimeout(() => setCopied(false), 2000);
									}}
									className="mt-2 flex w-full items-center justify-center gap-1.5 rounded border border-border py-1 text-[13px] text-text-muted transition-colors hover:border-accent hover:text-accent"
								>
									{copied ? (
										<Check className="h-3 w-3 text-accent-green" />
									) : (
										<Copy className="h-3 w-3" />
									)}
									{copied ? 'Copiato!' : 'Copia link'}
								</button>
							</div>

							{dialog.rateLimitSeconds && dialog.rateLimitSeconds > 0 ? (
								<div className="space-y-1.5">
									<p className="text-center text-[13px] text-text-muted">
										Limite raggiunto - riprova tra{' '}
										<span className="font-semibold text-text">{dialog.rateLimitSeconds}s</span>
									</p>
									<button
										type="button"
										onClick={() => {
											const subject = encodeURIComponent(`Invito ad accedere a ${appName}`);
											const body = encodeURIComponent(
												`Ciao${dialog.memberName ? ` ${dialog.memberName}` : ''},\n\nClicca il link qui sotto per accedere:\n\n${dialog.link}\n\nIl link è valido per 24 ore.\n\n${appName} - Comunità Energetica Rinnovabile`,
											);
											window.open(
												`mailto:${dialog.email}?subject=${subject}&body=${body}`,
												'_blank',
											);
										}}
										className="flex w-full items-center justify-center gap-2 rounded-md border border-accent py-2 text-xs font-semibold text-accent transition-colors hover:bg-accent/10"
									>
										<Mail className="h-3.5 w-3.5" />
										Invia con client email (alternativo)
									</button>
								</div>
							) : (
								<button
									type="button"
									disabled={dialog.sendingEmail}
									onClick={resendEmail}
									className="flex w-full items-center justify-center gap-2 rounded-md bg-accent py-2 text-xs font-semibold text-white transition-colors hover:bg-accent/90 disabled:opacity-40"
								>
									{dialog.sendingEmail ? (
										<Loader2 className="h-3.5 w-3.5 animate-spin" />
									) : (
										<Mail className="h-3.5 w-3.5" />
									)}
									Invia via Email
								</button>
							)}

							{typeof navigator !== 'undefined' && 'share' in navigator && (
								<button
									type="button"
									onClick={() =>
										navigator
											.share({
												title: 'Invito accesso CER',
												text: 'Sei stato invitato ad accedere alla piattaforma CER.',
												url: dialog.link ?? '',
											})
											.catch(() => {})
									}
									className="flex w-full items-center justify-center gap-2 rounded-md border border-border py-2 text-xs font-medium text-text-muted transition-colors hover:border-accent hover:text-accent"
								>
									<Share2 className="h-3.5 w-3.5" />
									Condividi con un&apos;altra app
								</button>
							)}
						</>
					)}

					<button
						type="button"
						onClick={() => setDialog((prev) => ({ ...prev, open: false }))}
						className="w-full py-1.5 text-xs text-text-muted hover:text-text"
					>
						Chiudi
					</button>
				</DialogContent>
			</Dialog>
		</>
	);
}

/** «28/09/2026 alle 16:50», ora di Roma. */
function formatSentAt(iso: string): string {
	const date = new Date(iso);
	if (Number.isNaN(date.getTime())) return '';
	const tz = { timeZone: 'Europe/Rome' } as const;
	const day = date.toLocaleDateString('it-IT', {
		...tz,
		day: '2-digit',
		month: '2-digit',
		year: 'numeric',
	});
	const time = date.toLocaleTimeString('it-IT', { ...tz, hour: '2-digit', minute: '2-digit' });
	return `${day} alle ${time}`;
}
